import buildRoleCapabilityVisiblePayload from '@/lib/settings/rolePermissions/buildRoleCapabilityVisiblePayload.js';
import buildVisibleMembersPayload from '@/lib/settings/members/buildVisibleMembersPayload.js';
import { createReducer } from '@/redux/lib/createReducer.js';
import { DEFAULT_GENERAL_OPEN_DESTINATION_OPTIONS, getDefaultPieveAppSettings } from '@/lib/pieveAppSettings.js';
import normalizeOpenDestinationOptions from '@/lib/settings/normalizeOpenDestinationOptions.js';
import mergeRoleCapabilityPayload from '@/lib/settings/rolePermissions/mergeRoleCapabilityPayload.js';
import buildUserCapabilityVisiblePayload from '@/lib/settings/userCapabilities/buildUserCapabilityVisiblePayload.js';
import mergeMemberRecords from '@/lib/settings/members/mergeMemberRecords.js';
import mergeUserCapabilityPayload from '@/lib/settings/userCapabilities/mergeUserCapabilityPayload.js';
import normalizeRoleRecord from '@/lib/settings/rolePermissions/normalizeRoleRecord.js';
import buildResetActiveCompanyScopeState from '@/redux/reducers/settingsReducer/buildResetActiveCompanyScopeState.js';
import { pickPieveSettingsByPersistence, toSafeString } from '@shared/utils';

const DEFAULT_LOCAL_ONLY_SETTINGS = Object.freeze(pickPieveSettingsByPersistence(getDefaultPieveAppSettings(), 'local'));
const EMPTY_ARRAY = Object.freeze([]);

const initialState = {
  local: {
    ...DEFAULT_LOCAL_ONLY_SETTINGS
  },
  openDestinationOptions: normalizeOpenDestinationOptions(DEFAULT_GENERAL_OPEN_DESTINATION_OPTIONS),
  roles: [],
  permissionCatalog: [],
  /* null until the grants have been read for the active organization; the Analytics card keeps its
     switches off limits while they are unknown. */
  rolePermissionGrants: null,
  /* The raw Teams setup check for the active organization (GET /v1/pieve/platforms/microsoft/health). */
  /* The raw parent–child organization overview for the active organization
     (GET /v1/pieve/organization/parent-links); null until it has been read. */
  organizationParentLinks: null,
  /* The raw API key list for the active organization (GET /v1/api-keys); null until it has been
     read. A key created here is prepended without its token, which lives only in the editor. */
  apiKeys: null,
  /* The raw active-key summary of one member (GET /v1/api-keys/created-by/:userId/summary), read
     when the remove-member dialog opens. */
  apiKeyCreatorSummariesByUserId: {},
  members: [],
  memberPendingMutations: [],
  memberServerPayload: [],
  memberLastLocalChangeAt: 0,
  roleCapabilitiesByRoleId: {},
  roleCapabilityPendingMutationsByRoleId: {},
  roleCapabilityServerPayloadByRoleId: {},
  roleCapabilityLastLocalChangeAtByRoleId: {},
  roleAppsByRoleId: {},
  roleAppModelsByRoleId: {},
  userCapabilityPayload: null,
  userCapabilityPendingMutations: [],
  userCapabilityServerPayload: null,
  userCapabilityLastLocalChangeAt: 0,
  userCapabilityAppsPayload: null,
  userCapabilityAppModelsByAppId: {}
};

export default createReducer(initialState, {
  RESET_PIEVE_ACTIVE_COMPANY_SCOPE: (state) => {
    return buildResetActiveCompanyScopeState({
      state,
      initialState
    });
  },

  GET_PIEVE_LOCAL_SETTINGS_SUCCESS: (state, payload) => {
    return {
      ...state,
      local: {
        ...DEFAULT_LOCAL_ONLY_SETTINGS,
        ...(payload || {})
      }
    };
  },

  PUT_PIEVE_LOCAL_SETTINGS_SUCCESS: (state, payload) => {
    return {
      ...state,
      local: {
        ...(state?.local || DEFAULT_LOCAL_ONLY_SETTINGS),
        ...(payload || {})
      }
    };
  },

  LIST_OPEN_DESTINATION_OPTIONS_SUCCESS: (state, payload) => {
    return {
      ...state,
      openDestinationOptions: payload || []
    };
  },

  GET_ORGANIZATION_PARENT_LINKS_SUCCESS: (state, payload) => {
    return {
      ...state,
      organizationParentLinks: payload?.data || null
    };
  },

  GET_API_KEYS_SUCCESS: (state, payload) => {
    return {
      ...state,
      apiKeys: Array.isArray(payload?.data) ? payload.data : []
    };
  },

  CREATE_API_KEY_SUCCESS: (state, payload) => {
    if (!payload?.data?.id) {
      return state;
    }

    const apiKey = { ...payload.data };
    delete apiKey.token;

    return {
      ...state,
      apiKeys: [apiKey, ...(state.apiKeys || EMPTY_ARRAY)]
    };
  },

  UPDATE_API_KEY_PERMISSIONS_SUCCESS: (state, payload) => {
    const updatedKey = payload?.data;

    if (!updatedKey?.id || !Array.isArray(state.apiKeys)) {
      return state;
    }

    return {
      ...state,
      apiKeys: state.apiKeys.map((key) => (key.id === updatedKey.id ? { ...key, accessLevel: updatedKey.accessLevel, permissions: updatedKey.permissions } : key))
    };
  },

  GET_API_KEY_CREATOR_SUMMARY_SUCCESS: (state, payload) => {
    const userId = toSafeString(payload?.meta?.userId);

    if (!userId) {
      return state;
    }

    return {
      ...state,
      apiKeyCreatorSummariesByUserId: { ...state.apiKeyCreatorSummariesByUserId, [userId]: payload?.data || null }
    };
  },

  /* A failed read drops the earlier answer, so the dialog never warns from a stale count. */
  GET_API_KEY_CREATOR_SUMMARY_FAIL: (state, payload) => {
    const userId = toSafeString(payload?.meta?.userId);

    if (!userId || !state.apiKeyCreatorSummariesByUserId?.[userId]) {
      return state;
    }

    const nextSummaries = { ...state.apiKeyCreatorSummariesByUserId };
    delete nextSummaries[userId];

    return {
      ...state,
      apiKeyCreatorSummariesByUserId: nextSummaries
    };
  },

  GET_ROLES_SUCCESS: (state, payload) => {
    return {
      ...state,
      roles: (Array.isArray(payload?.data) ? payload.data : []).map((role) => normalizeRoleRecord(role)).filter((role) => Boolean(role.id))
    };
  },

  GET_PERMISSION_CATALOG_SUCCESS: (state, payload) => {
    return {
      ...state,
      permissionCatalog: Array.isArray(payload?.data) ? payload.data : []
    };
  },

  GET_ROLE_PERMISSION_GRANTS_SUCCESS: (state, payload) => {
    return {
      ...state,
      rolePermissionGrants: Array.isArray(payload?.data) ? payload.data : []
    };
  },

  CREATE_ROLE_PERMISSION_GRANT_SUCCESS: (state, payload) => {
    const roleId = toSafeString(payload?.data?.roleId || payload?.meta?.roleId);
    const permissionId = toSafeString(payload?.data?.permissionId || payload?.meta?.permissionId);

    if (!roleId || !permissionId) {
      return state;
    }

    const currentGrants = Array.isArray(state?.rolePermissionGrants) ? state.rolePermissionGrants : [];

    if (currentGrants.some((grant) => grant.roleId === roleId && grant.permissionId === permissionId)) {
      return state;
    }

    return {
      ...state,
      rolePermissionGrants: [...currentGrants, { roleId, permissionId }]
    };
  },

  DELETE_ROLE_PERMISSION_GRANT_SUCCESS: (state, payload) => {
    const roleId = toSafeString(payload?.meta?.roleId);
    const permissionId = toSafeString(payload?.meta?.permissionId);

    if (!roleId || !permissionId) {
      return state;
    }

    return {
      ...state,
      rolePermissionGrants: (Array.isArray(state?.rolePermissionGrants) ? state.rolePermissionGrants : []).filter((grant) => !(grant.roleId === roleId && grant.permissionId === permissionId))
    };
  },

  CREATE_ROLE_SUCCESS: (state, payload) => {
    const nextRole = normalizeRoleRecord(payload?.data || {});

    if (!nextRole.id) {
      return state;
    }

    const currentRoles = Array.isArray(state?.roles) ? state.roles : [];
    const hasExistingRole = currentRoles.some((role) => role.id === nextRole.id);

    return {
      ...state,
      roles: hasExistingRole ? currentRoles.map((role) => (role.id === nextRole.id ? nextRole : role)) : [...currentRoles, nextRole]
    };
  },

  UPDATE_ROLE_SUCCESS: (state, payload) => {
    const nextRole = normalizeRoleRecord(payload?.data || {});

    if (!nextRole.id) {
      return state;
    }

    const currentRoles = Array.isArray(state?.roles) ? state.roles : [];
    const hasExistingRole = currentRoles.some((role) => role.id === nextRole.id);

    return {
      ...state,
      roles: hasExistingRole ? currentRoles.map((role) => (role.id === nextRole.id ? nextRole : role)) : [...currentRoles, nextRole]
    };
  },

  DELETE_ROLE_SUCCESS: (state, payload) => {
    const deletedRoleId = toSafeString(payload?.data?.id || payload?.meta?.roleId);

    if (!deletedRoleId) {
      return state;
    }

    const nextRoleAppsByRoleId = { ...(state?.roleAppsByRoleId || {}) };
    const nextRoleAppModelsByRoleId = { ...(state?.roleAppModelsByRoleId || {}) };
    const nextRoleCapabilitiesByRoleId = { ...(state?.roleCapabilitiesByRoleId || {}) };
    const nextRoleCapabilityPendingMutationsByRoleId = { ...(state?.roleCapabilityPendingMutationsByRoleId || {}) };
    const nextRoleCapabilityServerPayloadByRoleId = { ...(state?.roleCapabilityServerPayloadByRoleId || {}) };
    const nextRoleCapabilityLastLocalChangeAtByRoleId = { ...(state?.roleCapabilityLastLocalChangeAtByRoleId || {}) };
    delete nextRoleAppsByRoleId[deletedRoleId];
    delete nextRoleAppModelsByRoleId[deletedRoleId];
    delete nextRoleCapabilitiesByRoleId[deletedRoleId];
    delete nextRoleCapabilityPendingMutationsByRoleId[deletedRoleId];
    delete nextRoleCapabilityServerPayloadByRoleId[deletedRoleId];
    delete nextRoleCapabilityLastLocalChangeAtByRoleId[deletedRoleId];

    return {
      ...state,
      roles: (Array.isArray(state?.roles) ? state.roles : []).filter((role) => role.id !== deletedRoleId),
      roleCapabilitiesByRoleId: nextRoleCapabilitiesByRoleId,
      roleCapabilityPendingMutationsByRoleId: nextRoleCapabilityPendingMutationsByRoleId,
      roleCapabilityServerPayloadByRoleId: nextRoleCapabilityServerPayloadByRoleId,
      roleCapabilityLastLocalChangeAtByRoleId: nextRoleCapabilityLastLocalChangeAtByRoleId,
      roleAppsByRoleId: nextRoleAppsByRoleId,
      roleAppModelsByRoleId: nextRoleAppModelsByRoleId
    };
  },

  GET_MEMBERS_SUCCESS: (state, payload) => {
    const requestStartedAt = Number(payload?.meta?.requestStartedAt || 0);
    const lastLocalChangeAt = Number(state?.memberLastLocalChangeAt || 0);

    if (requestStartedAt > 0 && lastLocalChangeAt > 0 && requestStartedAt < lastLocalChangeAt) {
      return state;
    }

    const nextServerPayload = (Array.isArray(payload?.data) ? payload.data : []).filter((member) => Boolean(member?.userId || member?.email));
    const currentPendingMutations = Array.isArray(state?.memberPendingMutations) ? state.memberPendingMutations : EMPTY_ARRAY;

    return {
      ...state,
      members: buildVisibleMembersPayload({
        serverPayload: nextServerPayload,
        pendingMutations: currentPendingMutations
      }),
      memberServerPayload: nextServerPayload
    };
  },

  INVITE_MEMBERS_SUCCESS: (state, payload) => {
    const nextServerPayload = mergeMemberRecords({
      currentMembers: state?.memberServerPayload,
      nextMembers: payload?.data
    });

    return {
      ...state,
      members: mergeMemberRecords({
        currentMembers: state?.members,
        nextMembers: payload?.data
      }),
      memberServerPayload: nextServerPayload
    };
  },

  UPDATE_MEMBER_SUCCESS: (state, payload) => {
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);
    const currentPendingMutations = Array.isArray(state?.memberPendingMutations) ? state.memberPendingMutations : EMPTY_ARRAY;
    const nextPendingMutations = requestId ? currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId) : currentPendingMutations;
    const nextServerPayload = mergeMemberRecords({
      currentMembers: state?.memberServerPayload,
      nextMembers: [payload?.data]
    });

    return {
      ...state,
      members: buildVisibleMembersPayload({
        serverPayload: nextServerPayload,
        pendingMutations: nextPendingMutations
      }),
      memberPendingMutations: nextPendingMutations,
      memberServerPayload: nextServerPayload
    };
  },

  RESEND_MEMBER_INVITE_SUCCESS: (state, payload) => {
    const nextServerPayload = mergeMemberRecords({
      currentMembers: state?.memberServerPayload,
      nextMembers: [payload?.data]
    });

    return {
      ...state,
      members: mergeMemberRecords({
        currentMembers: state?.members,
        nextMembers: [payload?.data]
      }),
      memberServerPayload: nextServerPayload
    };
  },

  DELETE_MEMBER_SUCCESS: (state, payload) => {
    const deletedUserId = toSafeString(payload?.data?.userId || payload?.meta?.userId);

    if (!deletedUserId) {
      return state;
    }

    return {
      ...state,
      members: (Array.isArray(state?.members) ? state.members : []).filter((member) => member?.userId !== deletedUserId),
      memberServerPayload: (Array.isArray(state?.memberServerPayload) ? state.memberServerPayload : []).filter((member) => member?.userId !== deletedUserId),
      memberPendingMutations: (Array.isArray(state?.memberPendingMutations) ? state.memberPendingMutations : []).filter((mutation) => toSafeString(mutation?.member?.userId) !== deletedUserId)
    };
  },

  GET_ROLE_APPS_SUCCESS: (state, payload) => {
    const roleId = toSafeString(payload?.data?.role?.id || payload?.meta?.roleId);

    if (!roleId) {
      return state;
    }

    return {
      ...state,
      roleAppsByRoleId: {
        ...(state?.roleAppsByRoleId || {}),
        [roleId]: payload?.data || null
      }
    };
  },

  GET_ROLE_CAPABILITIES_SUCCESS: (state, payload) => {
    const roleId = toSafeString(payload?.data?.role?.id || payload?.meta?.roleId);
    const requestStartedAt = Number(payload?.meta?.requestStartedAt || 0);

    if (!roleId) {
      return state;
    }

    const lastLocalChangeAt = Number(state?.roleCapabilityLastLocalChangeAtByRoleId?.[roleId] || 0);

    if (requestStartedAt > 0 && lastLocalChangeAt > 0 && requestStartedAt < lastLocalChangeAt) {
      return state;
    }

    const currentPendingMutations = state?.roleCapabilityPendingMutationsByRoleId?.[roleId] || EMPTY_ARRAY;
    const nextServerPayload = payload?.data || null;

    return {
      ...state,
      roleCapabilitiesByRoleId: {
        ...(state?.roleCapabilitiesByRoleId || {}),
        [roleId]: buildRoleCapabilityVisiblePayload({
          serverPayload: nextServerPayload,
          pendingMutations: currentPendingMutations,
          fallbackRoleId: roleId
        })
      },
      roleCapabilityServerPayloadByRoleId: {
        ...(state?.roleCapabilityServerPayloadByRoleId || {}),
        [roleId]: nextServerPayload
      }
    };
  },

  GET_ROLE_APP_MODELS_SUCCESS: (state, payload) => {
    const roleId = toSafeString(payload?.data?.role?.id || payload?.meta?.roleId);
    const appId = toSafeString(payload?.data?.app?.id || payload?.meta?.appId);

    if (!roleId || !appId) {
      return state;
    }

    return {
      ...state,
      roleAppModelsByRoleId: {
        ...(state?.roleAppModelsByRoleId || {}),
        [roleId]: {
          ...((state?.roleAppModelsByRoleId || {})[roleId] || {}),
          [appId]: payload?.data || null
        }
      }
    };
  },

  GET_USER_CAPABILITIES_SUCCESS: (state, payload) => {
    const requestStartedAt = Number(payload?.meta?.requestStartedAt || 0);
    const lastLocalChangeAt = Number(state?.userCapabilityLastLocalChangeAt || 0);

    if (requestStartedAt > 0 && lastLocalChangeAt > 0 && requestStartedAt < lastLocalChangeAt) {
      return state;
    }

    const nextServerPayload = payload?.data || null;
    const currentPendingMutations = Array.isArray(state?.userCapabilityPendingMutations) ? state.userCapabilityPendingMutations : EMPTY_ARRAY;

    return {
      ...state,
      userCapabilityPayload: buildUserCapabilityVisiblePayload({
        serverPayload: nextServerPayload,
        pendingMutations: currentPendingMutations
      }),
      userCapabilityServerPayload: nextServerPayload
    };
  },

  GET_USER_CAPABILITY_APPS_SUCCESS: (state, payload) => {
    return {
      ...state,
      userCapabilityAppsPayload: payload?.data || null
    };
  },

  GET_USER_CAPABILITY_APP_MODELS_SUCCESS: (state, payload) => {
    const appId = toSafeString(payload?.data?.app?.id || payload?.meta?.appId);

    if (!appId) {
      return state;
    }

    return {
      ...state,
      userCapabilityAppModelsByAppId: {
        ...(state?.userCapabilityAppModelsByAppId || {}),
        [appId]: payload?.data || null
      }
    };
  },

  UPDATE_MEMBER_OPTIMISTIC: (state, payload) => {
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);
    const userId = toSafeString(payload?.meta?.userId || payload?.userId);
    const roleId = toSafeString(payload?.data?.roleId || payload?.roleId);
    const localChangeAt = Number(payload?.localChangeAt || payload?.meta?.localChangeAt || Date.now());

    if (!requestId || !userId || !roleId) {
      return state;
    }

    const currentMember = (Array.isArray(state?.members) ? state.members : []).find((member) => {
      return toSafeString(member?.userId) === userId;
    });

    if (!currentMember) {
      return state;
    }

    const currentPendingMutations = Array.isArray(state?.memberPendingMutations) ? state.memberPendingMutations : EMPTY_ARRAY;
    const nextPendingMutations = [
      ...currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId),
      {
        requestId,
        member: {
          ...currentMember,
          roleId
        }
      }
    ];

    return {
      ...state,
      members: buildVisibleMembersPayload({
        serverPayload: state?.memberServerPayload,
        pendingMutations: nextPendingMutations
      }),
      memberPendingMutations: nextPendingMutations,
      memberLastLocalChangeAt: localChangeAt
    };
  },

  UPDATE_MEMBER_FAIL: (state, payload) => {
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);

    if (!requestId) {
      return state;
    }

    const currentPendingMutations = Array.isArray(state?.memberPendingMutations) ? state.memberPendingMutations : EMPTY_ARRAY;
    const nextPendingMutations = currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId);

    return {
      ...state,
      members: buildVisibleMembersPayload({
        serverPayload: state?.memberServerPayload,
        pendingMutations: nextPendingMutations
      }),
      memberPendingMutations: nextPendingMutations
    };
  },

  UPDATE_ROLE_CAPABILITIES_OPTIMISTIC: (state, payload) => {
    const roleId = toSafeString(payload?.roleId || payload?.meta?.roleId);
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);
    const localChangeAt = Number(payload?.localChangeAt || payload?.meta?.localChangeAt || Date.now());
    const capabilitiesPatch = payload?.capabilities;

    if (!roleId || !requestId || !capabilitiesPatch) {
      return state;
    }

    const currentPendingMutations = Array.isArray(state?.roleCapabilityPendingMutationsByRoleId?.[roleId]) ? state.roleCapabilityPendingMutationsByRoleId[roleId] : [];
    const nextPendingMutations = [...currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId), { requestId, capabilities: capabilitiesPatch }];
    const currentServerPayload = state?.roleCapabilityServerPayloadByRoleId?.[roleId] || null;

    return {
      ...state,
      roleCapabilitiesByRoleId: {
        ...(state?.roleCapabilitiesByRoleId || {}),
        [roleId]: buildRoleCapabilityVisiblePayload({
          serverPayload: currentServerPayload,
          pendingMutations: nextPendingMutations,
          fallbackRoleId: roleId
        })
      },
      roleCapabilityPendingMutationsByRoleId: {
        ...(state?.roleCapabilityPendingMutationsByRoleId || {}),
        [roleId]: nextPendingMutations
      },
      roleCapabilityLastLocalChangeAtByRoleId: {
        ...(state?.roleCapabilityLastLocalChangeAtByRoleId || {}),
        [roleId]: localChangeAt
      }
    };
  },

  UPDATE_ROLE_CAPABILITIES_SUCCESS: (state, payload) => {
    const roleId = toSafeString(payload?.data?.role?.id || payload?.meta?.roleId);
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);

    if (!roleId) {
      return state;
    }

    const currentServerPayload = state?.roleCapabilityServerPayloadByRoleId?.[roleId] || null;
    const currentPendingMutations = Array.isArray(state?.roleCapabilityPendingMutationsByRoleId?.[roleId]) ? state.roleCapabilityPendingMutationsByRoleId[roleId] : [];
    const nextPendingMutations = requestId ? currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId) : currentPendingMutations;
    const nextServerPayload = mergeRoleCapabilityPayload({
      currentPayload: currentServerPayload,
      patchPayload: payload?.data || null,
      fallbackRoleId: roleId
    });

    return {
      ...state,
      roleCapabilitiesByRoleId: {
        ...(state?.roleCapabilitiesByRoleId || {}),
        [roleId]: buildRoleCapabilityVisiblePayload({
          serverPayload: nextServerPayload,
          pendingMutations: nextPendingMutations,
          fallbackRoleId: roleId
        })
      },
      roleCapabilityPendingMutationsByRoleId: {
        ...(state?.roleCapabilityPendingMutationsByRoleId || {}),
        [roleId]: nextPendingMutations
      },
      roleCapabilityServerPayloadByRoleId: {
        ...(state?.roleCapabilityServerPayloadByRoleId || {}),
        [roleId]: nextServerPayload
      }
    };
  },

  UPDATE_ROLE_CAPABILITIES_FAIL: (state, payload) => {
    const roleId = toSafeString(payload?.meta?.roleId);
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);

    if (!roleId || !requestId) {
      return state;
    }

    const currentServerPayload = state?.roleCapabilityServerPayloadByRoleId?.[roleId] || null;
    const currentPendingMutations = Array.isArray(state?.roleCapabilityPendingMutationsByRoleId?.[roleId]) ? state.roleCapabilityPendingMutationsByRoleId[roleId] : [];
    const nextPendingMutations = currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId);

    return {
      ...state,
      roleCapabilitiesByRoleId: {
        ...(state?.roleCapabilitiesByRoleId || {}),
        [roleId]: buildRoleCapabilityVisiblePayload({
          serverPayload: currentServerPayload,
          pendingMutations: nextPendingMutations,
          fallbackRoleId: roleId
        })
      },
      roleCapabilityPendingMutationsByRoleId: {
        ...(state?.roleCapabilityPendingMutationsByRoleId || {}),
        [roleId]: nextPendingMutations
      }
    };
  },

  UPDATE_USER_CAPABILITIES_OPTIMISTIC: (state, payload) => {
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);
    const localChangeAt = Number(payload?.localChangeAt || payload?.meta?.localChangeAt || Date.now());
    const capabilitiesPatch = payload?.capabilities;

    if (!requestId || !capabilitiesPatch) {
      return state;
    }

    const currentPendingMutations = Array.isArray(state?.userCapabilityPendingMutations) ? state.userCapabilityPendingMutations : [];
    const nextPendingMutations = [...currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId), { requestId, capabilities: capabilitiesPatch }];
    const currentServerPayload = state?.userCapabilityServerPayload || null;

    return {
      ...state,
      userCapabilityPayload: buildUserCapabilityVisiblePayload({
        serverPayload: currentServerPayload,
        pendingMutations: nextPendingMutations
      }),
      userCapabilityPendingMutations: nextPendingMutations,
      userCapabilityLastLocalChangeAt: localChangeAt
    };
  },

  UPDATE_USER_CAPABILITIES_SUCCESS: (state, payload) => {
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);
    const currentPendingMutations = Array.isArray(state?.userCapabilityPendingMutations) ? state.userCapabilityPendingMutations : [];
    const nextPendingMutations = requestId ? currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId) : currentPendingMutations;
    const currentServerPayload = state?.userCapabilityServerPayload || null;
    const nextServerPayload = mergeUserCapabilityPayload({
      currentPayload: currentServerPayload,
      patchPayload: payload?.data || null
    });

    return {
      ...state,
      userCapabilityPayload: buildUserCapabilityVisiblePayload({
        serverPayload: nextServerPayload,
        pendingMutations: nextPendingMutations
      }),
      userCapabilityPendingMutations: nextPendingMutations,
      userCapabilityServerPayload: nextServerPayload
    };
  },

  UPDATE_USER_CAPABILITIES_FAIL: (state, payload) => {
    const requestId = toSafeString(payload?.requestId || payload?.meta?.requestId);

    if (!requestId) {
      return state;
    }

    const currentPendingMutations = Array.isArray(state?.userCapabilityPendingMutations) ? state.userCapabilityPendingMutations : [];
    const nextPendingMutations = currentPendingMutations.filter((item) => toSafeString(item?.requestId) !== requestId);
    const currentServerPayload = state?.userCapabilityServerPayload || null;

    return {
      ...state,
      userCapabilityPayload: buildUserCapabilityVisiblePayload({
        serverPayload: currentServerPayload,
        pendingMutations: nextPendingMutations
      }),
      userCapabilityPendingMutations: nextPendingMutations
    };
  }
});
